/**
 * The promotional slots in both apps.
 *
 * Two audiences, and keeping them apart is what the screen is arranged around. The first
 * four placements are surfaces in the CUSTOMER app; the `VENDOR_` ones are inside the
 * vendor console, for announcements aimed at sellers — a fee change, a payout schedule, a
 * seasonal push to stock up. They are served by different endpoints and they read
 * completely differently, so mixing them in one undifferentiated list is how "your payout
 * schedule is changing" ends up on a shopper's home screen.
 *
 * The other thing it now leads with is whether a banner is actually on screen. A banner is
 * live only when it is switched on AND inside its date window, which is three fields an
 * admin had to combine in their head while looking at two raw timestamps.
 */
import { useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { MODULE_TYPE_OPTIONS, type VendorModuleType } from '@/features/bookings/types';
import { VENDOR_MODULE_TYPE_LABELS } from '@/features/vendors/types';
import { BANNER_FORMATS, BANNER_FORMAT_LABEL, type BannerFormat } from '@/features/banners/types';
import { API_BASE_URL } from '@/lib/api';
import {
  CalendarClock,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  MousePointerClick,
  Plus,
  Store,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DetailPanel, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import { EmptyState } from '@/components/ui/empty-state';
import { notify } from '@/components/ui/toaster';
import { SelectFilter } from '@/components/filters/filter-bar';
import { DateRangeFilter } from '@/components/filters/date-range-filter';
import { ConfirmModal } from '@/features/vendors/components/reason-modal';
import { ApiError } from '@/lib/api';
import { date_short, date_time } from '@/lib/format';
import { enum_param, string_param, use_url_state } from '@/hooks/use-url-state';
import {
  use_banner_list,
  use_create_banner,
  use_delete_banner,
  use_update_banner,
  use_upload_banner_image,
} from '@/features/banners/hooks';
import {
  AUDIENCE_LABEL,
  LINK_TYPES_FOR,
  LINK_TYPE_LABEL,
  PLACEMENT_LABEL,
  STATUS_TONE,
  placements_for,
  runs_during,
  status_label,
  window_summary,
  type AdminBanner,
  type BannerAudience,
  type BannerLinkType,
  type BannerPlacement,
} from '@/features/banners/types';

const AUDIENCES = ['CUSTOMER', 'VENDOR'] as const;

const AUDIENCE_COPY: Record<BannerAudience, { label: string; title: string; blurb: string }> = {
  CUSTOMER: {
    label: 'Shoppers',
    title: 'Banners in the customer app',
    blurb: 'Promotions on the home, food, services and health tabs',
  },
  VENDOR: {
    label: 'Vendors',
    title: 'Announcements in the vendor console',
    blurb: 'What the platform is telling its sellers — fees, payouts, policy, seasonal pushes',
  },
};

/** An `<input type="datetime-local">` value as the API wants it, or null. */
const to_iso = (v: string): string | null => (v ? new Date(v).toISOString() : null);

export function BannerListPage() {
  const [audience_raw, set_audience] = use_url_state('audience', enum_param(AUDIENCES));
  const audience: BannerAudience = audience_raw || 'CUSTOMER';
  const copy = AUDIENCE_COPY[audience];

  const [placement_filter, set_placement_filter] = use_url_state(
    'placement',
    enum_param(placements_for(audience)),
  );
  const [status_filter, set_status_filter] = use_url_state(
    'status',
    enum_param(['Live', 'Scheduled', 'Expired', 'Switched off'] as const),
  );
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);

  const [composing, set_composing] = useState(false);
  const [pending_delete, set_pending_delete] = useState<AdminBanner | null>(null);
  const [delete_error, set_delete_error] = useState<string | null>(null);

  /*
   * The whole set, unfiltered, and narrowed here.
   *
   * There are a handful of banners in total, and the tab counts have to describe the
   * audience you are NOT looking at — fetching per-audience made "Shoppers" read 0 while
   * standing on the vendor tab, which is worse than no count at all.
   */
  const { data, isLoading, error, refetch } = use_banner_list();
  const update_m = use_update_banner();
  const delete_m = use_delete_banner();

  const for_audience = useMemo(
    () => (data?.items ?? []).filter((b) => b.audience === audience),
    [data, audience],
  );

  const items = useMemo(
    () =>
      for_audience.filter(
        (b) =>
          (!placement_filter || b.placement === placement_filter) &&
          (!status_filter || status_label(b) === status_filter) &&
          runs_during(b, from, to),
      ),
    [for_audience, placement_filter, status_filter, from, to],
  );

  // The tiles describe the audience on screen, not the whole set.
  const metrics = useMemo(() => build_metrics(data ? for_audience : undefined), [data, for_audience]);

  async function toggle(b: AdminBanner) {
    try {
      await update_m.mutateAsync({ id: b.id, input: { is_active: !b.is_active } });
      notify.success(b.is_active ? 'Switched off' : 'Switched on', {
        description: b.is_active
          ? `"${b.title}" is off the app immediately.`
          : `"${b.title}" is on the app, subject to its dates.`,
      });
    } catch (err) {
      notify.error(err, 'Could not change this banner.');
    }
  }

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      tabs={
        <PageTabs
          current={audience}
          on_change={(v) => {
            set_audience(v as BannerAudience);
            // The placements belong to one audience, so a filter set on the other side
            // would silently return nothing.
            set_placement_filter('');
          }}
          tabs={AUDIENCES.map((a) => ({
            value: a,
            label: AUDIENCE_COPY[a].label,
            count: data?.items.filter((b) => b.audience === a).length,
          }))}
        />
      }
      actions={
        <Button size="sm" onClick={() => set_composing((v) => !v)} data-testid="banner-new">
          {composing ? (
            <>
              <X className="mr-1 h-4 w-4" /> Close
            </>
          ) : (
            <>
              <Plus className="mr-1 h-4 w-4" /> New banner
            </>
          )}
        </Button>
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {composing && (
        <BannerComposer audience={audience} on_done={() => set_composing(false)} />
      )}

      <DetailPanel
        title={`${AUDIENCE_LABEL[audience]} see these`}
        icon={audience === 'VENDOR' ? Store : Users}
        description={
          data
            ? `${items.length} of ${for_audience.length} shown · a banner is on screen only when it is switched on and inside its dates`
            : undefined
        }
        body_class="p-0"
        test_id="banner-list"
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <SelectFilter
              label="Placement"
              value={placement_filter}
              on_change={(v) => set_placement_filter(v as BannerPlacement)}
              options={placements_for(audience).map((p) => ({
                value: p,
                label: PLACEMENT_LABEL[p],
              }))}
              all_label="Anywhere"
              test_id="filter-placement"
            />
            {/*
              Status is worked out from three fields (switched on, starts, ends), so it is
              filtered here rather than asked of the server — which only knows the three.
            */}
            <SelectFilter
              label="Status"
              value={status_filter}
              on_change={(v) => set_status_filter(v)}
              options={[
                { value: 'Live', label: 'Live now' },
                { value: 'Scheduled', label: 'Scheduled' },
                { value: 'Expired', label: 'Expired' },
                { value: 'Switched off', label: 'Switched off' },
              ]}
              all_label="Any status"
              test_id="filter-status"
            />
            {/*
              On the run window rather than on created_at: a banner's dates ARE its
              schedule, so "what was on screen that week" is the question worth asking.
            */}
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
              }}
              label="Running during"
            />
          </div>
        }
      >
        {error ? (
          <div className="p-3.5">
            <ErrorState
              title="Could not load the banners"
              description="Nothing has been changed. Try again."
              action={
                <Button size="sm" variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              }
            />
          </div>
        ) : isLoading && !data ? (
          <div className="p-3.5">
            <PanelSkeleton rows={3} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-3.5" data-testid="banner-list-empty">
            <EmptyState
              icon={ImageIcon}
              title={
                placement_filter || status_filter
                  ? 'No banners match these filters'
                  : audience === 'VENDOR'
                    ? 'Nothing is being announced to vendors'
                    : 'No banners in the customer app'
              }
              description={
                audience === 'VENDOR'
                  ? 'Fee changes, payout schedules and seasonal pushes go here — vendors see them on their dashboard.'
                  : 'Promotions appear on the app tabs in the order you set.'
              }
            />
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="banner-list-rows">
            {items.map((b) => (
              <BannerRow
                key={b.id}
                banner={b}
                busy={update_m.isPending}
                on_toggle={() => void toggle(b)}
                on_delete={() => set_pending_delete(b)}
              />
            ))}
          </ul>
        )}
      </DetailPanel>

      {/*
        Delete used to fire straight from the row button. It is a hard delete with no undo
        and no soft-delete column — one stray click during QA destroyed a live banner and
        its creative was unrecoverable from both the database and disk.
      */}
      <ConfirmModal
        open={pending_delete !== null}
        title="Delete banner?"
        description={`"${pending_delete?.title ?? ''}" will be permanently deleted, along with its uploaded creative. This cannot be undone — switch it off instead if you only want it off the app.`}
        confirm_label={delete_m.isPending ? 'Deleting…' : 'Delete permanently'}
        loading={delete_m.isPending}
        error={delete_error}
        on_cancel={() => {
          set_pending_delete(null);
          set_delete_error(null);
        }}
        on_confirm={() => {
          if (!pending_delete) return;
          set_delete_error(null);
          delete_m.mutate(pending_delete.id, {
            onSuccess: () => set_pending_delete(null),
            onError: (err) =>
              set_delete_error(
                err instanceof ApiError ? err.message : 'Could not delete this banner',
              ),
          });
        }}
      />
    </DashboardLayout>
  );
}

function build_metrics(items: AdminBanner[] | undefined): Metric[] {
  const live = items?.filter((b) => status_label(b) === 'Live') ?? [];
  const scheduled = items?.filter((b) => status_label(b) === 'Scheduled') ?? [];
  const expired = items?.filter((b) => status_label(b) === 'Expired') ?? [];
  const off = items?.filter((b) => status_label(b) === 'Switched off') ?? [];

  return [
    {
      key: 'live',
      label: 'On screen now',
      value: items ? String(live.length) : undefined,
      hint: 'Switched on and in date',
      tone: live.length > 0 ? 'success' : 'default',
      icon: Eye,
      loading: !items,
    },
    {
      key: 'scheduled',
      label: 'Scheduled',
      value: items ? String(scheduled.length) : undefined,
      hint: 'Waiting for their start date',
      icon: CalendarClock,
      loading: !items,
    },
    {
      key: 'expired',
      label: 'Expired',
      // Worth surfacing: an expired banner is invisible but still occupies a slot in the
      // list, and its creative is still paid for in storage.
      value: items ? String(expired.length) : undefined,
      hint: expired.length ? 'Past their end date' : 'None run out',
      tone: expired.length > 0 ? 'warning' : 'default',
      icon: EyeOff,
      loading: !items,
    },
    {
      key: 'off',
      label: 'Switched off',
      value: items ? String(off.length) : undefined,
      hint: 'Off regardless of dates',
      icon: EyeOff,
      loading: !items,
    },
    {
      key: 'tappable',
      label: 'Tappable',
      value: items ? String(items.filter((b) => b.link_type !== 'NONE').length) : undefined,
      hint: 'The rest are picture only',
      icon: MousePointerClick,
      loading: !items,
    },
  ];
}

function BannerRow({
  banner,
  busy,
  on_toggle,
  on_delete,
}: {
  banner: AdminBanner;
  busy: boolean;
  on_toggle: () => void;
  on_delete: () => void;
}) {
  const status = status_label(banner);
  return (
    <li
      className="grid items-center gap-x-4 gap-y-2 px-3.5 py-3 lg:grid-cols-[7rem_1fr_10rem_auto]"
      data-testid={`banner-row-${banner.id}`}
    >
      {/* The creative is the thing being decided on, so it leads. */}
      <img
        src={`${API_BASE_URL}${banner.image_path}?variant=thumb`}
        alt=""
        className="h-12 w-24 shrink-0 rounded-control border border-border object-cover"
      />

      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate font-heading text-data font-bold" data-testid={`banner-title-${banner.id}`}>
          {banner.title}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone="neutral" size="sm">
            {PLACEMENT_LABEL[banner.placement]}
          </Badge>
          <Badge tone={banner.link_type === 'NONE' ? 'neutral' : 'info'} size="sm">
            {LINK_TYPE_LABEL[banner.link_type]}
          </Badge>
          {/* Only when it is NOT the default — every row saying "Full-width banner" is noise. */}
          {banner.format === 'ICON' && (
            <Badge tone="neutral" size="sm" data-testid={`banner-format-${banner.id}`}>
              {BANNER_FORMAT_LABEL.ICON}
            </Badge>
          )}
          <span className="text-micro text-muted-foreground">position {banner.display_order}</span>
        </span>
        {/*
          Only worth saying when it NARROWS the reach. "All vendors" is the default and printing
          it on every row would bury the handful that are actually targeted.
        */}
        {banner.audience === 'VENDOR' && (banner.target_modules ?? []).length > 0 && (
          <span className="text-micro text-muted-foreground" data-testid={`banner-modules-${banner.id}`}>
            Only {banner.target_modules.map((m) => VENDOR_MODULE_TYPE_LABELS[m] ?? m).join(', ')}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <Badge tone={STATUS_TONE[status]} size="sm" dot data-testid={`banner-status-${banner.id}`}>
          {status}
        </Badge>
        {/*
          The window in words. Two raw timestamps made an admin do date arithmetic to
          answer the one question the screen exists for: is this running?
        */}
        <span
          className="text-micro text-muted-foreground"
          title={[
            banner.starts_at ? `from ${date_time(banner.starts_at)}` : null,
            banner.ends_at ? `until ${date_time(banner.ends_at)}` : null,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {window_summary(banner)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={on_toggle}
          data-testid={`banner-toggle-${banner.id}`}
        >
          {banner.is_active ? (
            <>
              <EyeOff className="mr-1 h-4 w-4" /> Switch off
            </>
          ) : (
            <>
              <Eye className="mr-1 h-4 w-4" /> Switch on
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={on_delete}
          data-testid={`banner-delete-${banner.id}`}
          aria-label={`Delete ${banner.title}`}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </li>
  );
}

/**
 * Composing a banner.
 *
 * The audience is decided by the tab you are on rather than asked again here, so the
 * placement list and the link types on offer are already narrowed to what makes sense —
 * a vendor looking at their own dashboard is not shopping, so "opens a vendor" is simply
 * not there rather than there and rejected on save.
 */
function BannerComposer({
  audience,
  on_done,
}: {
  audience: BannerAudience;
  on_done: () => void;
}) {
  const file_input = useRef<HTMLInputElement>(null);
  const [placement, set_placement] = useState<BannerPlacement>(placements_for(audience)[0]);
  const [title, set_title] = useState('');
  const [format, set_format] = useState<BannerFormat>('BANNER');
  const [link_type, set_link_type] = useState<BannerLinkType>('NONE');
  const [link_value, set_link_value] = useState('');
  /** Empty = every vendor. VENDOR audience only. */
  const [target_modules, set_target_modules] = useState<VendorModuleType[]>([]);
  const [display_order, set_display_order] = useState('0');
  const [starts_at, set_starts_at] = useState('');
  const [ends_at, set_ends_at] = useState('');
  const [error, set_error] = useState<string | null>(null);

  const create_m = use_create_banner();
  const upload_m = use_upload_banner_image();
  const busy = create_m.isPending || upload_m.isPending;

  async function submit() {
    set_error(null);
    const file = file_input.current?.files?.[0];
    if (!file) return set_error('Choose an image for the banner.');
    if (!title.trim()) return set_error('Give the banner a title.');
    if (link_type !== 'NONE' && !link_value.trim()) {
      return set_error('This link type needs a target.');
    }
    try {
      const { image_s3_key } = await upload_m.mutateAsync(file);
      await create_m.mutateAsync({
        placement,
        format,
        title: title.trim(),
        image_s3_key,
        link_type,
        link_value: link_type === 'NONE' ? null : link_value.trim(),
        // Never sent on a customer placement: a shopper has no modules to match against.
        target_modules: audience === 'VENDOR' ? target_modules : [],
        display_order: Number(display_order) || 0,
        starts_at: to_iso(starts_at),
        ends_at: to_iso(ends_at),
      });
      notify.success('Banner created', {
        description: starts_at
          ? `Scheduled from ${date_short(to_iso(starts_at))}.`
          : `Live on ${PLACEMENT_LABEL[placement]} now.`,
      });
      on_done();
    } catch (e) {
      set_error(e instanceof Error ? e.message : 'Could not save the banner.');
    }
  }

  return (
    <DetailPanel
      title={`New ${AUDIENCE_LABEL[audience].toLowerCase()} banner`}
      icon={Plus}
      description={
        audience === 'VENDOR'
          ? 'Only vendors will see this, inside their console'
          : 'Everyone using the app will see this'
      }
      test_id="banner-composer"
    >
      <div className="grid gap-3 sm:grid-cols-2" data-testid="banner-form">
        <div className="grid gap-1.5">
          <Label htmlFor="b-placement">Where it appears</Label>
          <select
            id="b-placement"
            className="console-select"
            data-testid="banner-placement"
            value={placement}
            onChange={(e) => set_placement(e.target.value as BannerPlacement)}
          >
            {placements_for(audience).map((p) => (
              <option key={p} value={p}>
                {PLACEMENT_LABEL[p]}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="b-title">Title</Label>
          <input
            id="b-title"
            className="console-field"
            data-testid="banner-title"
            value={title}
            onChange={(e) => set_title(e.target.value)}
            placeholder={
              audience === 'VENDOR' ? 'Payout schedule changes 1 Sep' : 'Vaccination week'
            }
          />
        </div>

        {/*
          Both audiences. The app cannot pick a layout from the artwork alone, and getting it
          wrong is very visible: a hero squeezed into an icon slot is unreadable.
        */}
        <div className="grid gap-1.5">
          <Label htmlFor="b-format">How it is drawn</Label>
          <select
            id="b-format"
            className="console-select"
            data-testid="banner-format"
            value={format}
            onChange={(e) => set_format(e.target.value as BannerFormat)}
          >
            {BANNER_FORMATS.map((f) => (
              <option key={f} value={f}>
                {BANNER_FORMAT_LABEL[f]}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="b-link-type">What tapping it does</Label>
          <select
            id="b-link-type"
            className="console-select"
            data-testid="banner-link-type"
            value={link_type}
            onChange={(e) => {
              set_link_type(e.target.value as BannerLinkType);
              set_link_value('');
            }}
          >
            {LINK_TYPES_FOR[audience].map((t) => (
              <option key={t} value={t}>
                {LINK_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        {/*
          Vendors only. A pharmacy notice on a dog walker's dashboard is noise, and a strip that
          is mostly noise stops being read — which costs the one channel we have for the notices
          that do matter. Selecting nothing keeps the old behaviour (everyone), so a
          platform-wide announcement still needs no thought.
        */}
        {audience === 'VENDOR' && (
          <div className="grid gap-1.5">
            <Label>Who sees it</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="banner-target-modules">
              {MODULE_TYPE_OPTIONS.map((m) => {
                const on = target_modules.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    data-testid={`banner-module-${m}`}
                    aria-pressed={on}
                    onClick={() =>
                      set_target_modules((prev) =>
                        prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
                      )
                    }
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      on
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    )}
                  >
                    {VENDOR_MODULE_TYPE_LABELS[m] ?? m}
                  </button>
                );
              })}
            </div>
            <p className="text-micro text-muted-foreground">
              {target_modules.length === 0
                ? 'Every vendor will see this. Pick modules to narrow it.'
                : // "1 of 12 modules" — the noun agrees with the SET, not with how many are picked.
                  `Only ${target_modules.length} of ${MODULE_TYPE_OPTIONS.length} modules will see this.`}
            </p>
          </div>
        )}

        {link_type !== 'NONE' && (
          <div className="grid gap-1.5">
            <Label htmlFor="b-link-value">
              {link_type === 'VENDOR'
                ? 'Vendor ID'
                : link_type === 'MODULE'
                  ? 'Module'
                  : 'Link'}
            </Label>
            <input
              id="b-link-value"
              className="console-field"
              data-testid="banner-link-value"
              value={link_value}
              onChange={(e) => set_link_value(e.target.value)}
              placeholder={link_type === 'URL' ? 'https://…' : link_type === 'MODULE' ? 'VET' : ''}
            />
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="b-starts">Starts (optional)</Label>
          <input
            id="b-starts"
            type="datetime-local"
            className="console-field"
            data-testid="banner-starts-at"
            value={starts_at}
            onChange={(e) => set_starts_at(e.target.value)}
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="b-ends">Ends (optional)</Label>
          <input
            id="b-ends"
            type="datetime-local"
            className="console-field"
            data-testid="banner-ends-at"
            value={ends_at}
            onChange={(e) => set_ends_at(e.target.value)}
          />
          <p className="text-micro text-muted-foreground">
            Leave both empty to run until you switch it off.
          </p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="b-order">Position</Label>
          <input
            id="b-order"
            type="number"
            min={0}
            className="console-field w-24 tabular [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
            data-testid="banner-display-order"
            value={display_order}
            onChange={(e) => set_display_order(e.target.value)}
          />
          <p className="text-micro text-muted-foreground">Lower numbers come first.</p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="b-image">Creative</Label>
          <input
            id="b-image"
            ref={file_input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            data-testid="banner-image"
            className="cursor-pointer text-micro text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-control file:border file:border-border file:bg-secondary file:px-3 file:py-1.5 file:font-heading file:text-data file:font-semibold file:text-secondary-foreground hover:file:bg-muted"
          />
        </div>

        {error && (
          <div className="sm:col-span-2">
            <ErrorState title="Could not save" description={error} />
          </div>
        )}

        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <Button onClick={() => void submit()} disabled={busy} data-testid="banner-save">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Create banner
          </Button>
          <Button variant="ghost" onClick={on_done} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    </DetailPanel>
  );
}
